import { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { formatToLocalWithTZ } from '../../../constants/dateHelpers';
import { fetchLatestUpdateDate } from '../services/onTimeDispatchLatestUpdateApi';
import { logger } from '../../../../../utils/logger';

const FALLBACK_REFRESHED_LABEL = 'just now';

// Retry configuration for CORS/Network errors
const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_DELAY_MS: 1000,        // First retry after 1 second
  MAX_DELAY_MS: 8000,             // Cap at 8 seconds
  BACKOFF_MULTIPLIER: 2,          // Exponential: 1s -> 2s -> 4s -> 8s
};

const EASTERN_TZ_OFFSETS: Record<string, string> = {
  EST: '-05:00',
  EDT: '-04:00',
};

const TIMESTAMP_WITH_TZ_REGEX = /(?:[zZ]|[+-]\d{2}:?\d{2}|\s(?:EST|EDT))$/;
const TIMESTAMP_NO_TZ_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2})(?::(\d{2})(?::(\d{2})(\.(\d{1,3}))?)?)?)?$/;

function getNthWeekdayOfMonth(
  year: number,
  monthZeroBased: number,
  weekday: number,
  nth: number
): number {
  const firstDay = new Date(year, monthZeroBased, 1).getDay();
  const dayOffset = (weekday - firstDay + 7) % 7;
  return 1 + dayOffset + (nth - 1) * 7;
}

function getEasternOffsetForLocalDate(
  year: number,
  month: number,
  day: number,
  hour: number
): string {
  // US Eastern DST: second Sunday in March at 2:00 AM through
  // first Sunday in November at 2:00 AM.
  const secondSundayInMarch = getNthWeekdayOfMonth(year, 2, 0, 2);
  const firstSundayInNovember = getNthWeekdayOfMonth(year, 10, 0, 1);

  let isDst = false;

  if (month > 3 && month < 11) {
    isDst = true;
  } else if (month === 3) {
    isDst = day > secondSundayInMarch || (day === secondSundayInMarch && hour >= 2);
  } else if (month === 11) {
    isDst = day < firstSundayInNovember || (day === firstSundayInNovember && hour < 2);
  }

  return isDst ? EASTERN_TZ_OFFSETS.EDT : EASTERN_TZ_OFFSETS.EST;
}

function normalizeLatestUpdateTimestamp(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return trimmed;
  }

  const easternAbbrMatch = trimmed.match(/\s(EST|EDT)$/i);
  if (easternAbbrMatch) {
    const abbr = easternAbbrMatch[1].toUpperCase();
    const mappedOffset = EASTERN_TZ_OFFSETS[abbr] ?? EASTERN_TZ_OFFSETS.EST;
    return trimmed.replace(/\s(EST|EDT)$/i, mappedOffset);
  }

  if (TIMESTAMP_WITH_TZ_REGEX.test(trimmed)) {
    return trimmed;
  }

  const match = trimmed.match(TIMESTAMP_NO_TZ_REGEX);
  if (!match) {
    return trimmed;
  }

  const [
    ,
    yearStr,
    monthStr,
    dayStr,
    hourStr = '00',
    minuteStr = '00',
    secondStr = '00',
    milliRaw = '',
    milliDigits = ''
  ] = match;

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);

  const easternOffset = getEasternOffsetForLocalDate(year, month, day, hour);
  const normalizedMillis = milliRaw
    ? `.${milliDigits.padEnd(3, '0').slice(0, 3)}`
    : '';

  return `${yearStr}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}:${secondStr}${normalizedMillis}${easternOffset}`;
}

function parseLatestUpdateDate(raw: string): Date | null {
  const normalizedTimestamp = normalizeLatestUpdateTimestamp(raw);
  const parsed = new Date(normalizedTimestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Detect CORS/Network errors
function isCorsError(error: unknown): boolean {
  if (!error) return false;
  
  const errorStr = String(error);
  const isAxiosError = axios.isAxiosError(error);
  
  // Check for network error (status 0 = CORS/network issue)
  if (isAxiosError && error.code === 'ERR_NETWORK') {
    logger.warn('[LatestUpdate] Network error detected - likely CORS');
    return true;
  }
  
  // Check error message for CORS keywords
  const corsKeywords = ['CORS', 'ERR_FAILED', 'blocked by', 'preflight'];
  const hasCorsKeyword = corsKeywords.some(keyword =>
    errorStr.toLowerCase().includes(keyword.toLowerCase())
  );
  
  if (hasCorsKeyword) {
    logger.warn('[LatestUpdate] CORS error detected:', errorStr);
    return true;
  }
  
  return false;
}

// Calculate retry delay with exponential backoff
function calculateRetryDelay(attemptNumber: number): number {
  const baseDelay = RETRY_CONFIG.INITIAL_DELAY_MS * 
    Math.pow(RETRY_CONFIG.BACKOFF_MULTIPLIER, attemptNumber - 1);
  
  const delay = Math.min(baseDelay, RETRY_CONFIG.MAX_DELAY_MS);
  logger.log(`[LatestUpdate] Retry attempt ${attemptNumber}: waiting ${delay}ms`);
  return delay;
}

export interface UseOnTimeDispatchLatestUpdateReturn {
  latestUpdateDisplayValue: string;
  latestUpdateLoading: boolean;
  latestUpdateError: string | null;
}

export function formatLatestUpdateDisplayValue(
  latestUpdateDate: string | null | undefined
): string {
  if (!latestUpdateDate) {
    return FALLBACK_REFRESHED_LABEL;
  }

  const parsed = parseLatestUpdateDate(latestUpdateDate);
  if (!parsed) {
    return FALLBACK_REFRESHED_LABEL;
  }

  const formatted = formatToLocalWithTZ(parsed.toISOString());
  return formatted || FALLBACK_REFRESHED_LABEL;
}

export function useOnTimeDispatchLatestUpdate(): UseOnTimeDispatchLatestUpdateReturn {
  const [latestUpdateDisplayValue, setLatestUpdateDisplayValue] =
    useState<string>(FALLBACK_REFRESHED_LABEL);
  const [latestUpdateLoading, setLatestUpdateLoading] = useState(true);
  const [latestUpdateError, setLatestUpdateError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function for timeouts
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    retryCountRef.current = 0;

    // Async retry function
    const attemptFetch = async () => {
      try {
        logger.log(
          `[LatestUpdate] Fetching (attempt ${retryCountRef.current + 1}/${RETRY_CONFIG.MAX_RETRIES + 1})`
        );

        const response = await fetchLatestUpdateDate();

        if (!isMountedRef.current) {
          logger.log('[LatestUpdate] Component unmounted, ignoring response');
          return;
        }

        logger.log('[LatestUpdate] ✅ Success:', response.latestUpdateDate);
        setLatestUpdateDisplayValue(
          formatLatestUpdateDisplayValue(response.latestUpdateDate)
        );
        setLatestUpdateError(null);
        setLatestUpdateLoading(false);
      } catch (error: unknown) {
        if (!isMountedRef.current) {
          logger.log('[LatestUpdate] Component unmounted, ignoring error');
          return;
        }

        logger.error('[LatestUpdate] ❌ Error:', error);

        // Handle 404 - no retry needed
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          logger.log('[LatestUpdate] 404 error - endpoint not found, using fallback');
          setLatestUpdateDisplayValue(FALLBACK_REFRESHED_LABEL);
          setLatestUpdateError(null);
          setLatestUpdateLoading(false);
          return;
        }

        // Determine if we should retry
        const shouldRetry = isCorsError(error) && 
          retryCountRef.current < RETRY_CONFIG.MAX_RETRIES;

        if (shouldRetry) {
          retryCountRef.current++;
          const delayMs = calculateRetryDelay(retryCountRef.current);

          logger.warn(
            `[LatestUpdate] ⏳ Retrying in ${delayMs}ms (${retryCountRef.current}/${RETRY_CONFIG.MAX_RETRIES})`
          );

          // Schedule retry
          retryTimeoutRef.current = setTimeout(attemptFetch, delayMs);
        } else {
          // No more retries - handle final error
          const message = error instanceof Error
            ? error.message
            : 'Failed to load latest update date';

          logger.error('[LatestUpdate] 🛑 Max retries reached, using fallback');
          setLatestUpdateDisplayValue(FALLBACK_REFRESHED_LABEL);
          setLatestUpdateError(message);
          setLatestUpdateLoading(false);
        }
      }
    };

    attemptFetch();

    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    latestUpdateDisplayValue,
    latestUpdateLoading,
    latestUpdateError,
  };
}